import { useEffect, useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { api } from "../../../lib/api";

type RegexRule = {
	id: string;
	name: string;
	pattern: string;
	action: string;
	isEnabled: boolean;
	createdAt: Date;
	updatedAt: Date;
};

type RegexTestResult = {
	matched: boolean;
	action: "block" | "mask" | "allow";
	ruleName: string | null;
	finalContent: string;
};

type CharacterGroup = {
	id: string;
	name: string;
	chars: string;
	isEnabled: boolean;
	createdAt: Date;
	updatedAt: Date;
};

const EMPTY_FORM = {
	id: null as string | null,
	name: "",
	patterns: "",
	action: "block" as "block" | "mask",
	isEnabled: true,
};

const EMPTY_GROUP_FORM = {
	id: null as string | null,
	name: "",
	chars: "",
	isEnabled: true,
};

function getPatternLines(patterns: string) {
	return patterns
		.split("\n")
		.map((pattern) => pattern.trim())
		.filter(Boolean);
}

function getPatternPreview(patterns: string) {
	const lines = getPatternLines(patterns);
	const visible = lines.slice(0, 3).join(", ");
	const hiddenCount = Math.max(0, lines.length - 3);
	return hiddenCount > 0 ? `${visible} +${hiddenCount} more` : visible;
}

export function PatternBlacklistSection() {
	const [rules, setRules] = useState<RegexRule[]>([]);
	const [characterGroups, setCharacterGroups] = useState<CharacterGroup[]>([]);
	const [open, setOpen] = useState(false);
	const [groupOpen, setGroupOpen] = useState(false);
	const [confirmId, setConfirmId] = useState<string | null>(null);
	const [confirmGroupId, setConfirmGroupId] = useState<string | null>(null);
	const [form, setForm] = useState(EMPTY_FORM);
	const [groupForm, setGroupForm] = useState(EMPTY_GROUP_FORM);
	const [sampleMessage, setSampleMessage] = useState("");
	const [testResult, setTestResult] = useState<RegexTestResult | null>(null);
	const [isPatternBlacklistEnabled, setIsPatternBlacklistEnabled] = useState(true);
	const [isIPBlacklistEnabled, setIsIPBlacklistEnabled] = useState(true);
	const [isAIModerationEnabled, setIsAIModerationEnabled] = useState(false);
	const [isSplitTextDetectionEnabled, setIsSplitTextDetectionEnabled] = useState(true);
	const [splitWindowMessageCount, setSplitWindowMessageCount] = useState("10");
	const [splitWindowSeconds, setSplitWindowSeconds] = useState("30");
	const [splitMaxMessageLength, setSplitMaxMessageLength] = useState("10");
	const [aiBlockedMessageText, setAiBlockedMessageText] = useState("Message blocked by system");
	const [configSaved, setConfigSaved] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function load() {
		const { data } = await api.admin["pattern-blacklist"].all.get();
		if (data) setRules(data as RegexRule[]);
	}

	async function loadCharacterGroups() {
		const { data } = await api.admin["pattern-character-groups"].all.get();
		if (data) setCharacterGroups(data as CharacterGroup[]);
	}

	useEffect(() => {
		load();
		loadCharacterGroups();
		api.admin.config.get().then(({ data }) => {
			if (!data) return;
			setIsPatternBlacklistEnabled(data.isPatternBlacklistEnabled);
			setIsIPBlacklistEnabled(data.isIPBlacklistEnabled);
			setIsAIModerationEnabled(data.isAIModerationEnabled);
			setIsSplitTextDetectionEnabled(data.isSplitTextDetectionEnabled);
			setSplitWindowMessageCount(String(data.splitWindowMessageCount));
			setSplitWindowSeconds(String(data.splitWindowSeconds));
			setSplitMaxMessageLength(String(data.splitMaxMessageLength));
			setAiBlockedMessageText(data.aiBlockedMessageText);
		});
	}, []);

	async function handleCreate() {
		if (!form.name.trim() || !form.patterns.trim()) return;
		const payload = {
			name: form.name.trim(),
			patterns: form.patterns,
			action: form.action,
			isEnabled: form.isEnabled,
		};
		const { error } = form.id
			? await api.admin["pattern-blacklist"].update.put({ id: form.id, ...payload })
			: await api.admin["pattern-blacklist"].add.post(payload);
		if (error) {
			setError(form.id ? "failed to update patterns" : "failed to add patterns");
			return;
		}
		setError(null);
		setForm(EMPTY_FORM);
		setOpen(false);
		load();
	}

	function openAdd() {
		setForm(EMPTY_FORM);
		setOpen(true);
	}

	function openEdit(rule: RegexRule) {
		setForm({
			id: rule.id,
			name: rule.name,
			patterns: rule.pattern,
			action: rule.action as "block" | "mask",
			isEnabled: rule.isEnabled,
		});
		setOpen(true);
	}

	function openAddGroup() {
		setGroupForm(EMPTY_GROUP_FORM);
		setGroupOpen(true);
	}

	function openEditGroup(group: CharacterGroup) {
		setGroupForm({
			id: group.id,
			name: group.name,
			chars: group.chars,
			isEnabled: group.isEnabled,
		});
		setGroupOpen(true);
	}

	async function handleToggle(rule: RegexRule) {
		await api.admin["pattern-blacklist"].toggle.patch({ id: rule.id, enable: !rule.isEnabled });
		load();
	}

	async function handleDelete(id: string) {
		await api.admin["pattern-blacklist"].delete.delete({ id });
		setConfirmId(null);
		load();
	}

	async function handleSaveGroup() {
		if (!groupForm.name.trim() || !groupForm.chars.trim()) return;
		const payload = {
			name: groupForm.name.trim(),
			chars: groupForm.chars,
			isEnabled: groupForm.isEnabled,
		};
		const { error } = groupForm.id
			? await api.admin["pattern-character-groups"].update.put({ id: groupForm.id, ...payload })
			: await api.admin["pattern-character-groups"].add.post(payload);
		if (error) {
			setError(groupForm.id ? "failed to update character group" : "failed to add character group");
			return;
		}

		setError(null);
		setGroupForm(EMPTY_GROUP_FORM);
		setGroupOpen(false);
		loadCharacterGroups();
	}

	async function handleToggleGroup(group: CharacterGroup) {
		await api.admin["pattern-character-groups"].toggle.patch({ id: group.id, enable: !group.isEnabled });
		loadCharacterGroups();
	}

	async function handleDeleteGroup(id: string) {
		await api.admin["pattern-character-groups"].delete.delete({ id });
		setConfirmGroupId(null);
		loadCharacterGroups();
	}

	async function handleTest() {
		if (!sampleMessage.trim()) return;
		const { data } = await api.admin["pattern-blacklist"].test.post({ messageContent: sampleMessage });
		if (data) setTestResult(data as RegexTestResult);
	}

	async function handleConfigSave() {
		const splitCount = Number(splitWindowMessageCount);
		const splitSeconds = Number(splitWindowSeconds);
		const splitMaxLength = Number(splitMaxMessageLength);
		if (
			!Number.isInteger(splitCount) ||
			splitCount < 1 ||
			!Number.isInteger(splitSeconds) ||
			splitSeconds < 1 ||
			!Number.isInteger(splitMaxLength) ||
			splitMaxLength < 1
		) {
			setError("split-text settings must be positive whole numbers");
			return;
		}

		const { error } = await api.admin["update-blacklist-config"].patch({
			isPatternBlacklistEnabled,
			isIPBlacklistEnabled,
			isAIModerationEnabled,
			isSplitTextDetectionEnabled,
			splitWindowMessageCount: splitCount,
			splitWindowSeconds: splitSeconds,
			splitMaxMessageLength: splitMaxLength,
			aiBlockedMessageText,
		});

		if (error) {
			setError("failed to update pattern config");
			return;
		}

		setError(null);
		setConfigSaved(true);
		setTimeout(() => setConfigSaved(false), 2000);
	}

	return (
		<div className="space-y-4">
			<div className="grid gap-4 border border-[#111] p-3 md:grid-cols-2">
				<div className="flex items-center justify-between">
					<Label>Pattern Blacklist</Label>
					<Switch checked={isPatternBlacklistEnabled} onCheckedChange={setIsPatternBlacklistEnabled} />
				</div>
				<div className="flex items-center justify-between">
					<Label>Split Text Detection</Label>
					<Switch checked={isSplitTextDetectionEnabled} onCheckedChange={setIsSplitTextDetectionEnabled} />
				</div>
				<div className="space-y-2">
					<Label>Split Window Message Count</Label>
					<Input
						type="number"
						min={1}
						value={splitWindowMessageCount}
						onChange={(e) => setSplitWindowMessageCount(e.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label>Split Window Seconds</Label>
					<Input
						type="number"
						min={1}
						value={splitWindowSeconds}
						onChange={(e) => setSplitWindowSeconds(e.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label>Split Max Message Length</Label>
					<Input
						type="number"
						min={1}
						value={splitMaxMessageLength}
						onChange={(e) => setSplitMaxMessageLength(e.target.value)}
					/>
				</div>
				<div className="flex items-end">
					<Button size="sm" onClick={handleConfigSave}>
						{configSaved ? "saved" : "apply"}
					</Button>
				</div>
			</div>

			<div className="space-y-2 border border-[#111] p-3">
				<Label>Test Pattern Rules</Label>
				<textarea
					className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
					placeholder="Type a sample message"
					value={sampleMessage}
					onChange={(e) => setSampleMessage(e.target.value)}
				/>
				<div>
					<Button size="sm" onClick={handleTest}>
						test
					</Button>
				</div>
				{testResult && (
					<div className="text-xs font-mono text-[#00ffcc] space-y-1">
						<div>matched: {String(testResult.matched)}</div>
						<div>action: {testResult.action}</div>
						<div>rule: {testResult.ruleName ?? "-"}</div>
						<div>final: {testResult.finalContent}</div>
					</div>
				)}
			</div>

			<div className="space-y-3 border border-[#111] p-3">
				<div className="flex items-center justify-between">
					<p className="text-xs text-[#333] font-mono">{rules.length} rules</p>
					<Button size="sm" onClick={openAdd}>
						+ add patterns
					</Button>
				</div>
				{error && <p className="text-xs text-red-500 font-mono">{error}</p>}

				<Dialog open={open} onOpenChange={setOpen}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>{form.id ? "Edit Pattern Rule" : "Add Pattern Rule"}</DialogTitle>
						</DialogHeader>
						<div className="space-y-2">
							<Label>Name</Label>
							<Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} />
						</div>
						<div className="space-y-2">
							<Label>{form.action === "block" ? "Pattern(s) to block" : "Pattern(s) to mask"}</Label>
							<textarea
								className="min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
								value={form.patterns}
								onChange={(e) => setForm((s) => ({ ...s, patterns: e.target.value }))}
								placeholder={"spam.*\nscam\\d+\nabuse"}
							/>
						</div>
						<div className="grid grid-cols-2 gap-3">
							<div className="space-y-2">
								<Label>Action</Label>
								<select
									className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
									value={form.action}
									onChange={(e) => setForm((s) => ({ ...s, action: e.target.value as "block" | "mask" }))}
								>
									<option value="block">block</option>
									<option value="mask">mask</option>
								</select>
							</div>
						</div>
						<div className="space-y-2">
							<Label>Enabled</Label>
							<div className="h-9 flex items-center">
								<Switch checked={form.isEnabled} onCheckedChange={(v) => setForm((s) => ({ ...s, isEnabled: v }))} />
							</div>
						</div>
						<DialogFooter>
							<DialogClose asChild>
								<Button variant="ghost" size="sm">
									cancel
								</Button>
							</DialogClose>
							<Button size="sm" onClick={handleCreate}>
								{form.id ? "save" : "add"}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Name</TableHead>
							<TableHead>Patterns</TableHead>
							<TableHead>Action</TableHead>
							<TableHead>Status</TableHead>
							<TableHead>Enabled</TableHead>
							<TableHead className="w-20" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{rules.map((rule) => {
							const patterns = getPatternLines(rule.pattern);

							return (
								<TableRow key={rule.id}>
									<TableCell className="text-[#00ffcc]">{rule.name}</TableCell>
									<TableCell className="font-mono text-xs">
										<div className="space-y-1">
											<div className="text-[#333]">
												{patterns.length} pattern{patterns.length === 1 ? "" : "s"}
											</div>
											<div className="max-w-[620px] truncate text-[#00ffcc]">{getPatternPreview(rule.pattern)}</div>
										</div>
									</TableCell>
									<TableCell>{rule.action}</TableCell>
									<TableCell>
										<Badge variant={rule.isEnabled ? "destructive" : "muted"}>
											{rule.isEnabled ? "active" : "inactive"}
										</Badge>
									</TableCell>
									<TableCell>
										<Switch checked={rule.isEnabled} onCheckedChange={() => handleToggle(rule)} />
									</TableCell>
									<TableCell>
										{confirmId === rule.id ? (
											<div className="flex gap-1">
												<Button size="sm" variant="destructive" onClick={() => handleDelete(rule.id)}>
													confirm
												</Button>
												<Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>
													cancel
												</Button>
											</div>
										) : (
											<div className="flex gap-1">
												<Button size="sm" variant="ghost" onClick={() => openEdit(rule)}>
													edit
												</Button>
												<Button size="sm" variant="ghost" onClick={() => setConfirmId(rule.id)}>
													delete
												</Button>
											</div>
										)}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</div>

			<div className="space-y-3 border border-[#111] p-3">
				<div className="flex items-center justify-between">
					<p className="text-xs text-[#333] font-mono">{characterGroups.length} character groups</p>
					<Button size="sm" onClick={openAddGroup}>
						+ add group
					</Button>
				</div>

				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Name</TableHead>
							<TableHead>Characters</TableHead>
							<TableHead>Status</TableHead>
							<TableHead>Enabled</TableHead>
							<TableHead className="w-32" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{characterGroups.map((group) => (
							<TableRow key={group.id}>
								<TableCell className="text-[#00ffcc]">{group.name}</TableCell>
								<TableCell className="font-mono text-xs">{getPatternLines(group.chars).join(", ")}</TableCell>
								<TableCell>
									<Badge variant={group.isEnabled ? "destructive" : "muted"}>
										{group.isEnabled ? "active" : "inactive"}
									</Badge>
								</TableCell>
								<TableCell>
									<Switch checked={group.isEnabled} onCheckedChange={() => handleToggleGroup(group)} />
								</TableCell>
								<TableCell>
									{confirmGroupId === group.id ? (
										<div className="flex gap-1">
											<Button size="sm" variant="destructive" onClick={() => handleDeleteGroup(group.id)}>
												confirm
											</Button>
											<Button size="sm" variant="ghost" onClick={() => setConfirmGroupId(null)}>
												cancel
											</Button>
										</div>
									) : (
										<div className="flex gap-1">
											<Button size="sm" variant="ghost" onClick={() => openEditGroup(group)}>
												edit
											</Button>
											<Button size="sm" variant="ghost" onClick={() => setConfirmGroupId(group.id)}>
												delete
											</Button>
										</div>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>

			<Dialog open={groupOpen} onOpenChange={setGroupOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{groupForm.id ? "Edit Character Group" : "Add Character Group"}</DialogTitle>
					</DialogHeader>
					<div className="space-y-2">
						<Label>Name</Label>
						<Input value={groupForm.name} onChange={(e) => setGroupForm((s) => ({ ...s, name: e.target.value }))} />
					</div>
					<div className="space-y-2">
						<Label>Characters</Label>
						<textarea
							className="min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
							value={groupForm.chars}
							onChange={(e) => setGroupForm((s) => ({ ...s, chars: e.target.value }))}
							placeholder={"i\nI\n1\nl"}
						/>
					</div>
					<div className="space-y-2">
						<Label>Enabled</Label>
						<div className="h-9 flex items-center">
							<Switch
								checked={groupForm.isEnabled}
								onCheckedChange={(v) => setGroupForm((s) => ({ ...s, isEnabled: v }))}
							/>
						</div>
					</div>
					<DialogFooter>
						<DialogClose asChild>
							<Button variant="ghost" size="sm">
								cancel
							</Button>
						</DialogClose>
						<Button size="sm" onClick={handleSaveGroup}>
							{groupForm.id ? "save" : "add"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
